import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { StepName } from '../../../ui/utils/analytics/analyticsFlowTypes';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { buildCommitTransaction } from '../transaction-builders/buildCommitTransaction';
import { buildRestoreTransaction } from '../transaction-builders/buildRestoreTransaction';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

/**
 * Mutable state container for finalize handling.
 * Tracks re-entry guard during the two-dispatch pattern.
 */
export interface FinalizeViewState {
	isFinalizingInProgress: boolean;
}

/**
 * Creates the initial finalize view state.
 */
export function createFinalizeViewState(): FinalizeViewState {
	return {
		isFinalizingInProgress: false,
	};
}

/**
 * Handles finalizing streamed content using a two-dispatch pattern.
 *
 * This uses TWO separate dispatches to work correctly with track-changes:
 * 1. DISPATCH 1: Restore original content (streaming → original)
 * 2. DISPATCH 2: Commit final content (original → final)
 *
 * The two-dispatch pattern is critical for track-changes compatibility.
 *
 * @param view - The ProseMirror editor view
 * @param state - Current streaming plugin state
 * @param finalizeState - Mutable state container for finalize tracking
 * @param fireAnalyticsStep - Callback to fire the 'complete' analytics step (no target = finished)
 */
export function handleFinalize(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	finalizeState: FinalizeViewState,
	fireAnalyticsStep?: (step: StepName, targetStep?: StepName) => void,
): void {
	// Guard: Don't re-enter while we're dispatching restore/commit
	if (finalizeState.isFinalizingInProgress) {
		return;
	}

	const sessionState = aiSessionContextPluginKey.getState(view.state);
	if (!sessionState) {
		return;
	}

	// Check if finalize is needed:
	// - Session status is 'complete'
	// - Streaming state is in 'streaming' phase and actively streaming
	// - Final stream hasn't been applied yet
	const needsFinalize =
		sessionState.status === 'complete' &&
		state.streamPhase === 'streaming' &&
		state.isStreaming &&
		!state.hasFinalStream;

	if (!needsFinalize) {
		return;
	}

	// Set flag to prevent re-entry during dispatches
	finalizeState.isFinalizingInProgress = true;

	try {
		// DISPATCH 1: Restore original content (streaming → original)
		const restoreTr = buildRestoreTransaction(view.state);
		if (restoreTr) {
			view.dispatch(restoreTr);
		}

		// DISPATCH 2: Commit final content (original → final)
		const commitTr = buildCommitTransaction(view.state);
		if (commitTr) {
			view.dispatch(commitTr);
		}
		// Fire the 'complete' step with no target so that analyticsFlow sets finished=true
		// and dispatches the 'finished' userFlow event. This is the terminal step in the
		// xstate migration path — the loading step (fired in buildUpdateTransaction) transitions
		// to 'complete', and 'complete' with no target signals end-of-flow.
		fireAnalyticsStep?.('complete');
	} catch {
		// Finalize failed - continue
	} finally {
		finalizeState.isFinalizingInProgress = false;
	}
}
